import { Link } from 'react-router-dom';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 shadow-sm border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-gray-400 hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-white">
              Privacy Policy
            </h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gray-800 rounded-2xl shadow-lg p-8 text-gray-300">
          <p className="text-sm text-gray-400 mb-8">Last Updated: December 14, 2025</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="mb-4">
              SSD Intranet ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
              explains how we collect, use, and safeguard your information when you use our internal communication
              and resource management platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
            <p className="mb-4">We collect the following types of information:</p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Account Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Email address (via Google Workspace authentication)</li>
              <li>Name and profile picture (from your Google account)</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">SMS Notification Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Phone number (when you opt in to SMS notifications)</li>
              <li>SMS opt-in status and preferences</li>
              <li>Timestamp of opt-in/opt-out actions</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Usage Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access logs and timestamps</li>
              <li>Features and resources accessed</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use your information to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Authenticate your access to the SSD Intranet</li>
              <li>Send SMS notifications about important updates (if opted in)</li>
              <li>Provide access to internal resources and documents</li>
              <li>Improve our services and user experience</li>
              <li>Maintain security and prevent unauthorized access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. SMS Communications</h2>
            <p className="mb-4">
              If you opt in to receive SMS notifications:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your phone number is used solely for sending SSD Intranet notifications</li>
              <li>We do not sell, rent, or share your phone number with third parties</li>
              <li>Message frequency varies based on system updates and alerts</li>
              <li>Standard message and data rates may apply</li>
              <li>You can opt out at any time by visiting the SMS Notifications page or replying STOP</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Data Sharing</h2>
            <p className="mb-4">
              We do not sell or rent your personal information. We may share your information only:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Within our organization for legitimate business purposes</li>
              <li>With service providers who assist in operating our platform (under strict confidentiality)</li>
              <li>When required by law or to protect our legal rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational measures to protect your information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Secure authentication via Google Workspace</li>
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular security reviews and updates</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. Data Retention</h2>
            <p className="mb-4">
              We retain your information for as long as your account is active or as needed to provide services.
              SMS opt-in records are retained for compliance purposes and can be deleted upon request.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Your Rights</h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Opt out of SMS notifications at any time</li>
              <li>Request deletion of your data (subject to legal requirements)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. Changes to This Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes
              by posting the new policy on this page with an updated effective date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">10. Contact Us</h2>
            <p className="mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact your
              system administrator or IT department.
            </p>
          </section>

          <div className="mt-12 pt-8 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              See also: <Link to="/terms" className="text-blue-400 hover:text-blue-300">Terms and Conditions</Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-400 border-t border-gray-700">
        <p>&copy; {new Date().getFullYear()} SSD Intranet. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PrivacyPage;
